import { hasConsent, CONSENT_CHANGED_EVENT } from "@/lib/legal/consent";
// lib/gtag.ts

import { isInternalTraffic } from "@/lib/internal-traffic";
import { projectStudyFinderGaParams } from "@/lib/study-finder-ga-params";

/**
 * ---------------------------------------------------------------------------
 * Google Analytics 4 helper
 * ---------------------------------------------------------------------------
 * A tiny, zero-dependency wrapper that lets any file (Server or Client) emit
 * GA4 hits without sprinkling `window.gtag( … )` everywhere.
 *
 * 1.  **GA_ID** is read from `NEXT_PUBLIC_GA_ID`.
 *     - When the env var is missing (local dev, test, etc.) all helpers
 *       become silent no-ops, so you can import them unconditionally.
 *
 * 2.  **pageview(path)**
 *     Sends a manual `page_view` event with the current page metadata.
 *     Call it after every client-side route change.
 *
 * 3.  **event(action, params)**
 *     General-purpose wrapper for custom events,
 *     e.g. `event('whatsapp_click', { category: 'CTA', label: 'Header' })`.
 *
 * 4.  **Type safety**
 *     We cast `globalThis` to a `WindowWithGtag` interface so TypeScript
 *     recognises `window.gtag`, but we avoid using `any` and keep the helper
 *     tree-shakable on the server.
 *
 * ---------------------------------------------------------------------------
 * Example
 * ---------------------------------------------------------------------------
 *   import { pageview, event } from "@/lib/gtag"
 *
 *   // Next.js App Router (Client Component)
 *   useEffect(() => pageview(router.pathname), [router.pathname])
 *
 *   // Custom CTA click
 *   const handleClick = () => {
 *     event("whatsapp_click", { category: "CTA", label: "Header" })
 *     window.open("https://wa.me/6590065997", "_blank")
 *   }
 */

/** Your GA4 Measurement ID (public) */
export const GA_ID = process.env.NEXT_PUBLIC_GA_ID || "";
export const GA_READY_EVENT = "eclat-ga-ready";

type GtagFn = (...args: [string, ...unknown[]]) => void;
type PageViewPayload = {
  blog_entry_mode?: string;
  blog_layout_version?: string;
  content_group: string;
  page_path: string;
  page_location?: string;
  page_referrer?: string;
  page_title?: string;
};

type PageMeasurementContext = {
  blog_entry_mode?: string;
  blog_layout_version?: string;
};

interface WindowWithGtag extends Window {
  __eclatGa4Loaded?: boolean;
  gtag?: GtagFn;
}

const window = globalThis as unknown as WindowWithGtag;
let lastTrackedPageLocation: string | null = null;
let pendingEvents: Array<{ action: string; params: Record<string, unknown> }> =
  [];
let pendingUserProperties: Record<string, string> = {};
export const MAX_PENDING_GA_EVENTS = 100;

function clearPendingAnalytics() {
  pendingEvents = [];
  pendingUserProperties = {};
  lastTrackedPageLocation = null;
}
function analyticsAllowed() {
  if (hasConsent("analytics")) return true;
  clearPendingAnalytics();
  return false;
}
// Consent can change without another analytics call; erase pre-revocation queues.
if (typeof globalThis.addEventListener === "function") {
  globalThis.addEventListener(CONSENT_CHANGED_EVENT, () => {
    analyticsAllowed();
  });
}

function sendSafely(...args: Parameters<GtagFn>): boolean {
  try {
    if (typeof window.gtag !== "function") return false;
    window.gtag(...args);
    return true;
  } catch {
    // Do not recursively report analytics failures through analytics.
    return false;
  }
}

export function isGaReady(): boolean {
  return window.__eclatGa4Loaded === true;
}

function canSendNow(): boolean {
  return isGaReady() && typeof window.gtag === "function";
}

function getPageMeasurementContext(): PageMeasurementContext {
  const documentRef = (window as Window & { document?: Document }).document;
  if (typeof documentRef?.querySelector !== "function") return {};
  const contextRoot = documentRef?.querySelector<HTMLElement>(
    "[data-ga-layout-version][data-ga-entry-mode]"
  );
  if (!contextRoot) return {};

  return {
    ...(contextRoot.dataset.gaLayoutVersion
      ? { blog_layout_version: contextRoot.dataset.gaLayoutVersion }
      : {}),
    ...(contextRoot.dataset.gaEntryMode
      ? { blog_entry_mode: contextRoot.dataset.gaEntryMode }
      : {}),
  };
}

export function flushPendingGaOperations(): void {
  if (!GA_ID || !analyticsAllowed()) return;
  if (isInternalTraffic()) {
    pendingEvents = [];
    pendingUserProperties = {};
    return;
  }
  if (!canSendNow()) return;

  const queuedEvents = pendingEvents;
  pendingEvents = [];

  for (const queuedEvent of queuedEvents) {
    sendSafely("event", queuedEvent.action, queuedEvent.params);
  }

  if (Object.keys(pendingUserProperties).length > 0) {
    sendSafely("set", "user_properties", pendingUserProperties);
    pendingUserProperties = {};
  }
}

/** Derive a GA4 content group from the pathname. */
export function getContentGroup(pathname: string): string {
  if (pathname === "/") return "home";
  if (pathname.startsWith("/blog/scholarships/matcher")) return "tools";
  if (pathname === "/blog") return "blog";
  if (pathname.startsWith("/blog/")) return "blog";
  if (pathname.startsWith("/jae-cut-off-points")) return "tools";
  if (pathname.startsWith("/l1r5-calculator")) return "tools";
  if (pathname.startsWith("/elr2b2-calculator")) return "tools";
  if (pathname.startsWith("/uas70-calculator")) return "tools";
  if (pathname.startsWith("/bursary-eligibility-calculator")) return "tools";
  if (pathname.startsWith("/dsa-talent-areas")) return "tools";
  if (pathname.startsWith("/study-places-near-me")) return "tools";
  if (pathname.startsWith("/study-rooms")) return "tools";
  if (pathname.startsWith("/study-cafes")) return "tools";
  if (pathname.startsWith("/landing/")) return "landing";
  if (pathname.startsWith("/legal/")) return "legal";
  if (pathname.startsWith("/support")) return "support";
  if (pathname.startsWith("/media-partnerships")) return "partnerships";
  if (pathname === "/media" || pathname.startsWith("/media/")) return "media";
  if (pathname === "/videos" || pathname.startsWith("/videos/"))
    return "videos";
  if (pathname.startsWith("/partnerships")) return "partnerships";
  return "other";
}

/** Standard page-view */
export const pageview = (url: string) => {
  if (!GA_ID || !analyticsAllowed()) return;
  if (isInternalTraffic()) return;
  if (!isGaReady()) return;
  if (!window.gtag) return;

  const hasWindowLocation = typeof window.location !== "undefined";
  const hasDocumentTitle =
    typeof (window as Window & { document?: Document }).document?.title ===
    "string";
  const hasDocumentReferrer =
    typeof (window as Window & { document?: Document }).document?.referrer ===
    "string";

  let page_path = url;
  let page_location: string | undefined;

  if (hasWindowLocation) {
    try {
      const resolved = new URL(url, window.location.origin);
      // Reporting model: treat query-string changes as filter interactions (events),
      // not separate pages.
      resolved.search = "";
      resolved.hash = "";
      page_path = resolved.pathname;
      page_location = resolved.toString();
    } catch {
      // Fall back to the raw string if it is not a URL/relative URL.
    }
  }

  const page_referrer =
    lastTrackedPageLocation ??
    (hasDocumentReferrer ? window.document?.referrer : undefined);

  const payload: PageViewPayload = {
    page_path,
    content_group: getContentGroup(page_path),
    ...getPageMeasurementContext(),
    ...(page_location ? { page_location } : {}),
    ...(hasDocumentTitle
      ? { page_title: window.document?.title ?? undefined }
      : {}),
    ...(page_referrer ? { page_referrer } : {}),
  };

  const sent = sendSafely("event", "page_view", payload);
  if (sent && page_location) {
    lastTrackedPageLocation = page_location;
  }
};

/** Convenience wrapper for custom events */
export const event = (action: string, params: Record<string, unknown> = {}) => {
  if (!GA_ID || !analyticsAllowed()) return;
  if (isInternalTraffic()) return;
  const safeParams: Record<string, unknown> = {
    ...getPageMeasurementContext(),
    ...params,
  };
  // Internal placement labels must not populate GA's campaign source field.
  // Explicit campaign_details events retain their documented source meaning.
  if (action !== "campaign_details" && "source" in safeParams) {
    const source = safeParams.source;
    delete safeParams.source;
    safeParams.interaction_source ??= source;
  }
  const contextualParams = projectStudyFinderGaParams(safeParams);
  if (!canSendNow()) {
    // GA may never load when blocked. Retain only the latest bounded history.
    if (pendingEvents.length >= MAX_PENDING_GA_EVENTS) pendingEvents.shift();
    pendingEvents.push({ action, params: contextualParams });
    return;
  }

  sendSafely("event", action, contextualParams);
};

/** Set GA4 user properties for audience segmentation */
export const setUserProperties = (props: Record<string, string>) => {
  if (!GA_ID || !analyticsAllowed()) return;
  if (isInternalTraffic()) return;
  if (!canSendNow()) {
    pendingUserProperties = {
      ...pendingUserProperties,
      ...props,
    };
    return;
  }

  sendSafely("set", "user_properties", props);
};

/* ---------- TS type helpers (optional) ---------- */
declare global {
  interface Window {
    // Google's gtag function - we only need the 'event' and 'config' signatures
    gtag?: GtagFn;
  }
}
